import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import moment from 'moment';
import { Button, FormInstance, Input, InputRef, notification, Row, Space, Tag, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import Icon, { SearchOutlined } from '@ant-design/icons';

import { ISimuladorResponse } from './interfaces';

import { EmitirErro } from '../../services/EmitirErro';
import { SairDoSistema } from '../../services/LStorage/SairSistema';
import { convertDataUSParaBrasilEHora } from '../../services/Conversores';
import ResponseService from '../../services/http/simulador/ResponseService';
import { EItemFormatType, EItemType, IItem } from '../SimuladorInteligente/interfaces';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { removeAcento } from '../../utils/RemoveAcentos';
import { formatValue } from '../../utils/simulador';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { FilterConfirmProps } from 'antd/lib/table/interface';
import FluxoService from '../../services/http/simulador/FluxoService';
import { TEXTO_LINHA_DE_CREDITO_NAO_ENCONTRADA } from '.';

const IconSearch = (props: any) => <Icon {...props} component={FaSearch} ></Icon>

export const STATUS_OPTIONS = [
    {
        text: 'Finalizado',
        value: 'finalizado',
        color: '#17751b',
    },
    {
        text: 'Em preenchimento',
        value: 'em_preenchimento',
        color: '#2db7f5',
    }
];

const FIRST_COLUMNS_ORDER = [
    'nome da empresa',
    'cnpj',
    'nome',
    'linha de crédito'
];

const DEFAULT_VISIBLE_COLUMNS = [
    'nome da empresa',
    'cnpj',
    'nome',
    'linha de crédito',
    'iniciado em',
    'status'
];

interface IColumn {
    type: EItemType;
    id: string;
    format: EItemFormatType | undefined;
    fieldName?: string;
    name: string;
}

export const useRespostasSimulador = () => {
    const [itens, setItens] = useState<IItem[]>([]);
    const [responses, setResponses] = useState<ISimuladorResponse[]>([]);
    const [visibleColumns, setVisibleColumns] = useState<ColumnsType<ISimuladorResponse>>([]);
    const [isColumnsConfigModalVisible, setIsColumnsConfigModalVisible] = useState(false);
    const [isResponseModalVisible, setIsResponseModalVisible] = useState(false);
    const [responseModalData, setResponseModalData] = useState<ISimuladorResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const handleSearch = useCallback((confirm: (param?: FilterConfirmProps) => void): void => {
        confirm();
    }, []);

    const handleReset = useCallback((clearFilters: any): void => {
        clearFilters();
    }, []);

    const searchInputRef = useRef<InputRef>(null);

    const navigate = useNavigate();

    const refContagemDoFiltro = useRef<Array<any>>([]);
    const formRef = useRef<FormInstance>(null);

    const allItems = useMemo(() => {

        function flattenItems(items: IItem[]): IItem[] {
            const result: IItem[] = [];

            function traverse(item: IItem) {
                const { itens = [], ...rest } = item;

                // Adiciona o item sem os filhos primeiro
                result.push({ ...rest });

                // Recursivamente adiciona os filhos
                for (const child of itens) {
                    traverse(child);
                }
            }

            for (const item of items) {
                traverse(item);
            }

            return result;
        }

        return flattenItems(itens);
    }, [itens]);

    const availableColumns = useMemo(() => {

        const itemsNames: {
            [key: string]: {
                type: EItemType,
                id: string,
                format: EItemFormatType | undefined;
                fieldName?: string;
                allow_multiple_options?: boolean;
            };
        } = {};

        allItems
            .filter((e) => ![EItemType.MESSAGE, EItemType.COMPARISON].includes(e.type))
            .forEach((e) => {
                if (e.type === EItemType.COLLECT) {
                    e.fields?.forEach((field) => {
                        itemsNames[field.label] = {
                            type: e.type,
                            id: e.id,
                            format: field.format,
                            fieldName: field.name,
                        };
                    });

                    return;
                }

                if (e.table_display_name) {
                    itemsNames[e.table_display_name] = {
                        type: e.type,
                        id: e.id,
                        format: e.format,
                        allow_multiple_options: e.type === EItemType.OPTIONS ? e.allow_multiple_options : undefined
                    };
                }
            });

        const finalColumns = Object.entries(itemsNames).map(([name, data]) => {
            return {
                name,
                ...data,
            }
        })

        return finalColumns;
    }, [allItems]);

    const getColumnSearchProps = useCallback((column: IColumn) => {
        if (![EItemType.COLLECT, EItemType.FIELD].includes(column.type)) {
            return;
        }

        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={searchInputRef}
                        placeholder={`Digite...`}
                        value={selectedKeys[0]}
                        onChange={(e: any) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={(e) => { e.stopPropagation(); handleSearch(confirm); }}
                        style={{
                            marginBottom: 8,
                            display: 'block'
                        }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(confirm)}
                            icon={<SearchOutlined />}
                            size="small"
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => filtered ? <IconSearch style={{ color: filtered ? '#1890ff' : undefined, fontSize: filtered ? 14 : undefined }} /> : <SearchOutlined />,
            onFilter: (value: string | number | boolean, record: ISimuladorResponse) => {
                const columnData = record.respostas.find((e) => e.id === column.id);

                if (columnData?.type === EItemType.COLLECT) {
                    if (!columnData?.response) {
                        return false;
                    }

                    const data = columnData?.response.find((e) => column.fieldName === e.name)?.value;

                    if (!data) {
                        return false;
                    }

                    let trecord = removeAcento(String(data));

                    return trecord.includes(removeAcento(String(value)));
                }

                const data = columnData?.response as string;

                let trecord = removeAcento(data);

                return trecord.includes(removeAcento(String(value)));
            },
            onFilterDropdownVisibleChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => searchInputRef.current?.select());
                }
            },
        }
    }, [handleReset, handleSearch]);

    const getFilters = useCallback((column: IColumn) => {
        if (![EItemType.OPTIONS, EItemType.RESULT].includes(column.type)) {
            return;
        }

        if (column.type === EItemType.OPTIONS) {
            return {
                filters: allItems.find((e) => e.id === column.id)?.options?.map((e) => ({
                    text: e.text,
                    value: e.text,
                })),
                onFilter: (value: any, record: ISimuladorResponse) => {
                    const responseItem = record.respostas.find((response) => response.id === column.id);

                    if (responseItem?.type === EItemType.OPTIONS) {
                        return !!responseItem.response?.includes(value);
                    }

                    return false;
                }
            }
        }

        const setOfResults = new Set(allItems.filter((e) => e.table_display_name === column.name).map((e) => e.line_of_credit)) as Set<string>;

        return {
            filters: Array.from(setOfResults).sort((a, b) => a?.localeCompare(b)).map((line_of_credit) => ({
                text: line_of_credit || '',
                value: line_of_credit || '',
            })),
            onFilter: (value: any, record: ISimuladorResponse) => {
                const columnValue = record.respostas.find((response) => response.type === EItemType.RESULT)?.result;

                return columnValue === value;
            }
        }
    }, [allItems]);

    const getSorter = useCallback((column: IColumn) => {
        if (column.type === EItemType.COLLECT) {
            return {
                sorter: (a: ISimuladorResponse, b: ISimuladorResponse) => {
                    const columnA = a.respostas.find((e) => e.id === column.id);
                    const columnB = b.respostas.find((e) => e.id === column.id);

                    if (columnA?.type !== EItemType.COLLECT || !columnA.response) {
                        return 1;
                    }

                    if (columnB?.type !== EItemType.COLLECT || !columnB.response) {
                        return -1;
                    }

                    const valueColumnA = columnA?.response.find((e) => column.fieldName === e.name)?.value;
                    const valueColumnB = columnB?.response.find((e) => column.fieldName === e.name)?.value;

                    if (typeof valueColumnA === "string") {
                        return valueColumnA?.localeCompare(String(valueColumnB));
                    }

                    return 1;
                }
            }
        }

        if (column.type === EItemType.OPTIONS) {
            return {
                sorter: (a: ISimuladorResponse, b: ISimuladorResponse) => {
                    const columnA = a.respostas.find((e) => e.id === column.id);
                    const columnB = b.respostas.find((e) => e.id === column.id);

                    if (!columnA?.response && !columnB?.response) {
                        return 0;
                    }

                    if (!columnA?.response) {
                        return 1;
                    }

                    if (!columnB?.response) {
                        return -1;
                    }

                    if (
                        columnA?.type !== EItemType.OPTIONS ||
                        columnB?.type !== EItemType.OPTIONS
                    ) {
                        return 1;
                    }

                    return columnA.response.join(", ").localeCompare(columnB.response.join(", "));
                }
            }
        }

        if (column.type === EItemType.FIELD) {
            return {
                sorter: (a: ISimuladorResponse, b: ISimuladorResponse) => {
                    const columnA = a.respostas.find((e) => e.id === column.id);
                    const columnB = b.respostas.find((e) => e.id === column.id);

                    if (column.format === EItemFormatType.CURRENCY || typeof columnA?.response === "number" || typeof columnB?.response === "number") {
                        return (Number(columnA?.response || 0) - Number(columnB?.response || 0))
                    }

                    if (typeof columnA?.response === "string" || typeof columnB?.response === "string") {
                        if (typeof columnA?.response === "string") {
                            return columnA?.response?.localeCompare(String(columnB?.response));
                        }

                        return 1;
                    }

                    return 1;
                }
            }
        }

        if (column.type === EItemType.RESULT) {
            return {
                sorter: (a: ISimuladorResponse, b: ISimuladorResponse) => {
                    const columnA = a.respostas.find((e) => e.table_display_name === column.name);
                    const columnB = b.respostas.find((e) => e.table_display_name === column.name);

                    if (typeof columnA?.result === "string" || typeof columnB?.result === "string") {
                        if (typeof columnA?.result === "string") {
                            return columnA?.result?.localeCompare(String(columnB?.result));
                        }

                        return 1;
                    }

                    return 1;
                }
            }
        }
    }, []);

    const formattedColumns = useMemo(() => {
        return availableColumns.map((column) => ({
            title: column.name,
            dataIndex: column.name,
            width: "10%",
            showSorterTooltip: false,
            ...getColumnSearchProps(column),
            ...getFilters(column),
            ...getSorter(column),
            render: (_: any, record: ISimuladorResponse) => {

                if (column.type === EItemType.RESULT) {
                    const resultColumn = record.respostas.find((e) => e.table_display_name === column.name);

                    return resultColumn?.result || '--';
                }

                const columnData = record.respostas.find((e) => e.id === column.id);

                if (columnData?.type === EItemType.COLLECT) {
                    if (!columnData?.response) {
                        return '--';
                    }

                    const fieldData = columnData?.response.find((e) => column.fieldName === e.name);
                    return fieldData?.format ? (formatValue({ format: fieldData.format, value: fieldData.value }) || '--') : (fieldData?.value || '--');
                }

                if (columnData?.type === EItemType.OPTIONS) {
                    if (!columnData?.response) {
                        return '--';
                    }

                    if (!column.allow_multiple_options) {
                        return columnData.response[0];
                    }

                    return (
                        <>
                            {columnData.response.map((e) => <Tag color={record.respostas.find((e) => e.result === TEXTO_LINHA_DE_CREDITO_NAO_ENCONTRADA) ? "red" : undefined} >{e}</Tag>)}
                        </>
                    )
                }

                return columnData?.format ? (formatValue({ format: columnData.format, value: columnData.response }) || '--') : (columnData?.response || '--');
            }
        }));
    }, [availableColumns, getColumnSearchProps, getFilters, getSorter]);

    const allAvailableColumnsTitles = useMemo(() => {
        return [
            ...formattedColumns.map((value) => value.title),
            "Iniciado em",
            "Finalizado em",
            "Status",
        ].sort((a, b) => a.localeCompare(b));
    }, [formattedColumns]);

    const handleOpenColumnsConfigModal = useCallback(() => {
        setIsColumnsConfigModalVisible(true);
    }, []);

    const handleCloseColumnsConfigModal = useCallback(() => {
        setIsColumnsConfigModalVisible(false);
    }, []);

    useEffect(() => {
        // TODO: Verificar permissão
        // let arrayUrl = window.location.pathname.split("/");
        // VerificaRota(arrayUrl, navigate, true, validacaoPermissao(['view_limites_e_taxas']));
    }, []);

    const handleOpenResponseModal = useCallback(() => {
        setIsResponseModalVisible(true);
    }, []);

    const handleCloseResponseModal = useCallback(() => {
        setIsResponseModalVisible(false);
    }, []);

    const handleClickViewResponse = useCallback((item: ISimuladorResponse): void => {
        handleOpenResponseModal();
        setResponseModalData(item)
    }, [handleOpenResponseModal]);

    const handleChangeVisibleColumns = useCallback((
        columns: ColumnsType<ISimuladorResponse>,
        columnsTitles: string[],
    ) => {
        const prioridade = new Map();
        FIRST_COLUMNS_ORDER.forEach((item, index) => prioridade.set(item, index));

        const prioritarios = columns
            .filter(col => prioridade.has(String(col.title).toLowerCase()))
            .sort((a, b) => prioridade.get(String(a.title).toLowerCase()) - prioridade.get(String(b.title).toLowerCase()));

        const restantes = columns.filter(col => !prioridade.has(String(col.title).toLowerCase()));

        const otherColumns = [
            {
                title: 'Status',
                dataIndex: 'status',
                width: '4%',
                filters: STATUS_OPTIONS,
                onFilter: (value: any, record: ISimuladorResponse) => {
                    return record.status === value;
                },
                render: (val: any) => {
                    const statusEncontrado = STATUS_OPTIONS.find((e) => e.value === val);

                    return (
                        <span>
                            <Tag color={statusEncontrado?.color}>
                                {statusEncontrado ? statusEncontrado.text : val}
                            </Tag>
                        </span>
                    )
                },
            },
            {
                title: "Iniciado em",
                dataIndex: "createdAt",
                width: "10%",
                sorter: (a: any, b: any) => {

                    let aMonMen: any = null
                    let bMonMen: any = null
                    if (a.createdAt) {
                        aMonMen = moment(a.createdAt).unix()
                    } else {
                        aMonMen = 0
                    }
                    if (b.createdAt) {
                        bMonMen = moment(b.createdAt).unix()
                    } else {
                        bMonMen = 0
                    }

                    return aMonMen - bMonMen
                },
                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            ref={searchInputRef}
                            placeholder={`Digite...`}
                            value={selectedKeys[0]}
                            onChange={(e: any) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                            onPressEnter={(e) => { e.stopPropagation(); handleSearch(confirm); }}
                            style={{
                                marginBottom: 8,
                                display: 'block'
                            }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => handleSearch(confirm)}
                                icon={<SearchOutlined />}
                                size="small"
                            >
                                Pesquisar
                            </Button>
                            <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                                Limpar
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: any) => filtered ? <IconSearch style={{ color: filtered ? '#1890ff' : undefined, fontSize: filtered ? 14 : undefined }} /> : <SearchOutlined />,
                onFilter: (value: string | number | boolean, record: ISimuladorResponse) => {
                    if (typeof value !== "string") {
                        return false;
                    }

                    let texto: string | undefined = ''
                    if (record.createdAt) {
                        texto = convertDataUSParaBrasilEHora(record.createdAt)
                    }

                    return (
                        texto ? texto.toString().toLowerCase().includes(value.toLowerCase()) : false
                    )
                },
                onFilterDropdownVisibleChange: (visible: any) => {
                    if (visible) {
                        setTimeout(() => searchInputRef.current?.select());
                    }
                },
                showSorterTooltip: false,
                render: (value: string) => (
                    convertDataUSParaBrasilEHora(value) || '--'
                )
            },
            {
                title: "Finalizado em",
                dataIndex: "fluxo_finalizado_em",
                width: "10%",
                sorter: (a: any, b: any) => {

                    let aMonMen: any = null
                    let bMonMen: any = null
                    if (a.fluxo_finalizado_em) {
                        aMonMen = moment(a.fluxo_finalizado_em).unix()
                    } else {
                        aMonMen = 0
                    }
                    if (b.fluxo_finalizado_em) {
                        bMonMen = moment(b.fluxo_finalizado_em).unix()
                    } else {
                        bMonMen = 0
                    }

                    return aMonMen - bMonMen
                },
                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            ref={searchInputRef}
                            placeholder={`Digite...`}
                            value={selectedKeys[0]}
                            onChange={(e: any) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                            onPressEnter={(e) => { e.stopPropagation(); handleSearch(confirm); }}
                            style={{
                                marginBottom: 8,
                                display: 'block'
                            }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => handleSearch(confirm)}
                                icon={<SearchOutlined />}
                                size="small"
                            >
                                Pesquisar
                            </Button>
                            <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                                Limpar
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: any) => filtered ? <IconSearch style={{ color: filtered ? '#1890ff' : undefined, fontSize: filtered ? 14 : undefined }} /> : <SearchOutlined />,
                onFilter: (value: string | number | boolean, record: ISimuladorResponse) => {
                    if (typeof value !== "string") {
                        return false;
                    }

                    let texto: string | undefined = ''
                    if (record.fluxo_finalizado_em) {
                        texto = convertDataUSParaBrasilEHora(record.fluxo_finalizado_em)
                    }

                    return (
                        texto ? texto.toString().toLowerCase().includes(value.toLowerCase()) : false
                    )
                },
                onFilterDropdownVisibleChange: (visible: any) => {
                    if (visible) {
                        setTimeout(() => searchInputRef.current?.select());
                    }
                },
                showSorterTooltip: false,
                render: (value: string) => (
                    convertDataUSParaBrasilEHora(value) || '--'
                )
            },
        ]

        const orderedColumns = [
            ...prioritarios,
            ...restantes,
            ...otherColumns.filter((column) => columnsTitles.includes(column.title.toLocaleLowerCase())),
            {
                title: "Visualizar",
                dataIndex: "id",
                width: "1%",
                render: (_: string, record: ISimuladorResponse) => (
                    <Row>
                        <Tooltip title="Visualizar" color="blue" key="visualizarblue">
                            <Button
                                type="primary"
                                style={{ marginLeft: 5 }}
                                onClick={() => handleClickViewResponse(record)}
                            >
                                <SearchOutlined />
                            </Button>
                        </Tooltip>
                    </Row>
                )
            }
        ];

        setVisibleColumns(orderedColumns);
    }, [handleClickViewResponse, handleReset, handleSearch]);

    const handleSubmitColumnsConfig = useCallback(() => {
        formRef.current?.validateFields()
            .then((values: any) => {
                const checkedEntries = Object.entries(values).filter(([_, checked]) => checked);

                const checkedColumns = checkedEntries.map(([columnTitle]) => {
                    const column = formattedColumns.find((e) => e.title === columnTitle);

                    return column || null;
                }).filter((column) => !!column) as ColumnType<ISimuladorResponse>[];

                const checkedColumnsTitles = checkedEntries.map(([columnTitle]) => columnTitle.toLowerCase());

                handleChangeVisibleColumns(checkedColumns, checkedColumnsTitles);
                handleCloseColumnsConfigModal();
                localStorage.setItem("@DesenvolveSP:simuladorColunasRespostas", JSON.stringify(checkedColumnsTitles));
            })
            .catch(() => {
                notification.error({
                    message: 'Erro',
                    description:
                        'Preencha os campos corretamente!',
                });
            });
    }, [formattedColumns, handleChangeVisibleColumns, handleCloseColumnsConfigModal]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                const [
                    itensList,
                    responsesList
                ] = await Promise.all([
                    FluxoService.getItems(controller.signal),
                    ResponseService.getResponses(),
                ]);

                setItens(itensList);
                setResponses(responsesList);

                setIsLoading(false);
            } catch (error) {
                let msgErro: any = (error as Error);
                setIsLoading(false);

                if (msgErro?.message === 'USUáRIO NãO AUTORIZADO.') {
                    SairDoSistema();
                    notification.error({
                        message: 'Sessão expirada',
                        description:
                            'Sua sessão expirou, realize o login novamente!',
                    });
                    navigate('/');
                } else {
                    EmitirErro(error, true, 'respostas_simulador_dkwdfaiweyi')
                }
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, [navigate]);

    useEffect(() => {
        const storedDefaultColumns = localStorage.getItem("@DesenvolveSP:simuladorColunasRespostas");

        const columnsTitles = storedDefaultColumns ? JSON.parse(storedDefaultColumns) : DEFAULT_VISIBLE_COLUMNS;

        const defaultColumns = formattedColumns.filter((column) => {
            return columnsTitles.includes(column.title.toLocaleLowerCase());
        });

        handleChangeVisibleColumns(defaultColumns, columnsTitles);
    }, [formattedColumns, handleChangeVisibleColumns]);

    return {
        isLoading,
        refContagemDoFiltro,
        isResponseModalVisible,
        handleCloseResponseModal,
        responseModalData,
        formattedColumns,
        responses,
        visibleColumns,
        isColumnsConfigModalVisible,
        handleOpenColumnsConfigModal,
        handleCloseColumnsConfigModal,
        handleSubmitColumnsConfig,
        formRef,
        allAvailableColumnsTitles,
    }
}
